import { useState, useEffect, useMemo } from 'react';

import { useSearchParams, useParams } from 'react-router-dom';

import { LiveTranscription } from './components/LiveTranscription';
import { usePlaybackController } from './components/PlaybackController';
import { PlaybackTranscription } from './components/PlaybackTranscription';
import { useScrollToBottom } from './hooks/useScrollToBottom';
import { useSpeakerManagement } from './hooks/useSpeakerManagement';
import { useTranscription } from './hooks/useTranscription';
import { useTranscriptInconsistenciesStore } from './stores/transcriptInconsistenciesStore';
import { LiveTranscriptBlock } from './types/transcriptTypes';
import { TranscriptStatus } from '../../../../../backend/src/case/transcription/transcript-enums';
import { ErrorPage } from '../../../components/molecules/ErrorPages/ErrorPage';
import { PageName } from '@/analytics/events';
import { trackPageView } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { useGetTranscript } from '@/api/queries/useGetTranscripts';
import { TranscriptInconsistency } from '@/api/types/backend-dto-types';

const Transcription = () => {
  const [searchParams] = useSearchParams();
  const { transcriptId } = useParams();
  const caseId = searchParams.get('caseId');

  const [storeUpdateTrigger, setStoreUpdateTrigger] = useState(0);

  const { data: savedTranscript, isLoading: isLoadingTranscript } = useGetTranscript(transcriptId ?? null, caseId);
  const isPlaybackMode = savedTranscript?.status === TranscriptStatus.Completed;

  const { initializeInconsistencies, getInconsistencies } = useTranscriptInconsistenciesStore();

  const isLoadingMode = Boolean(transcriptId && isLoadingTranscript);

  // Track page view
  useEffect(() => {
    trackPageView(PageName.Transcription);
  }, []);

  const audioData =
    caseId && transcriptId
      ? { url: `${APIBaseChronos}/api/case/${caseId}/transcripts/${transcriptId}/audio/full` }
      : null;

  useEffect(() => {
    const unsubscribe = useTranscriptInconsistenciesStore.subscribe(() => {
      setStoreUpdateTrigger((prev) => prev + 1);
    });
    return unsubscribe;
  }, []);

  // Initialize inconsistencies store with saved transcript data
  useEffect(() => {
    if (savedTranscript?.id && savedTranscript.inconsistencies) {
      initializeInconsistencies(savedTranscript.id, savedTranscript.inconsistencies);
    }
  }, [savedTranscript?.id, savedTranscript?.inconsistencies?.length, initializeInconsistencies]);

  const liveTranscription = useTranscription(caseId, transcriptId, savedTranscript);

  const playbackController = usePlaybackController({
    audioUrl: audioData?.url,
  });

  const {
    isRecording = false,
    isPaused = false,
    isConnected = false,
    isCompleting = false,
    blocks = [],
    logicalSpeakers = [],
    internalSpeakers = [],
    startRecording = undefined,
    pauseRecording = undefined,
    completeRecording = undefined,
    mapInternalSpeaker = undefined,
    acceptDiarizationCorrection = undefined,
    rejectDiarizationCorrection = undefined,
    updateBlock = undefined,
  } = liveTranscription ?? {};

  const sortedInconsistencies = useMemo(() => {
    const storeInconsistencies = transcriptId ? getInconsistencies(transcriptId) : [];

    if (isPlaybackMode && blocks && storeInconsistencies.length > 0) {
      return [...storeInconsistencies].sort((a, b) => {
        const getEarliestTimestamp = (inconsistency: TranscriptInconsistency) => {
          const referencedBlocks = inconsistency.transcriptSubfact?.referencedBlocks || [];
          if (referencedBlocks.length === 0) return Infinity;

          const timestamps = referencedBlocks
            .map((refBlock: { id: string }) => {
              const transcriptBlock = blocks.find((block) => block.id === refBlock.id);
              return transcriptBlock ? Number(transcriptBlock.timestamp) : Infinity;
            })
            .filter((timestamp: number) => timestamp !== Infinity);

          return timestamps.length > 0 ? Math.min(...timestamps) : Infinity;
        };

        return getEarliestTimestamp(a) - getEarliestTimestamp(b);
      });
    }

    return storeInconsistencies;
  }, [storeUpdateTrigger, isPlaybackMode, blocks]);

  // Get internal speaker ids for blocks with only internal assignment
  const blockInternalSpeakerIds = blocks
    .filter((block) => !block.internalSpeaker?.speaker)
    .map((block) => block.internalSpeakerId);

  // Internal speakers only exist in live mode, these are the speakers that are not mapped to a logical speaker e.g. Guest-1
  const displayInternalSpeakers = internalSpeakers.filter((i) => blockInternalSpeakerIds.includes(i.id));

  const { transcriptContainerRef, isUserScrolling, handleScroll, scrollToBottom, scrollToTranscriptBlock } =
    useScrollToBottom(isPlaybackMode ? [] : blocks);

  const { editingSpeaker, editingName, startEditingSpeaker, cancelEditingSpeaker, saveSpeakerMapping, setEditingName } =
    useSpeakerManagement(
      logicalSpeakers,
      displayInternalSpeakers,
      mapInternalSpeaker || (() => {}),
      caseId,
      transcriptId || null,
    );

  const handleBlockClick = (block: LiveTranscriptBlock) => {
    if (isPlaybackMode && playbackController) {
      playbackController.seekTo(Number(block.timestamp));
    } else {
      scrollToTranscriptBlock(block.id);
    }
  };

  const handleScrollToBlockFromInconsistency = (blockId: string) => {
    console.log(isPlaybackMode, blocks);

    if (isPlaybackMode && blocks) {
      const block = blocks.find((b) => b.id === blockId);
      if (block) {
        // Navigate to that transcript block & set the appropriate time on the playback
        const blockElement = document.getElementById(`block-${blockId}`);
        if (blockElement) {
          blockElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        if (playbackController) {
          playbackController.seekTo(Number(block.timestamp));
        }
      }
    } else {
      scrollToTranscriptBlock(blockId);
    }
  };

  if (!savedTranscript) {
    if (isLoadingTranscript) return null;
    return (
      <ErrorPage
        title="No transcript found"
        description="We could not find this transcript, please refresh the page. If this issue persists, reach out to the Wexler team."
      />
    );
  }

  return isPlaybackMode ? (
    <PlaybackTranscription
      // Local transcript state
      displayBlocks={blocks}
      displayLogicalSpeakers={logicalSpeakers}
      displayInconsistencies={sortedInconsistencies}
      // Transcript metadata
      transcriptMetadata={{
        otherSpeakers: savedTranscript?.otherSpeakers,
        deponent: savedTranscript?.deponent,
        createdDate: savedTranscript?.createdDate,
        transcriptName: savedTranscript?.name,
        phraseList: savedTranscript?.phraseList,
        objective: savedTranscript?.objective,
        summary: savedTranscript?.summary,
        speakers: savedTranscript?.speakers,
      }}
      playbackController={playbackController}
      transcriptContainerRef={transcriptContainerRef}
      handleScroll={handleScroll}
      handleBlockClick={handleBlockClick}
      handleScrollToBlockFromInconsistency={handleScrollToBlockFromInconsistency}
      updateBlock={updateBlock}
      acceptDiarizationCorrection={acceptDiarizationCorrection}
      rejectDiarizationCorrection={rejectDiarizationCorrection}
      isLoadingMode={isLoadingMode}
    />
  ) : (
    <LiveTranscription
      // Local transcript state
      displayBlocks={blocks}
      displayLogicalSpeakers={logicalSpeakers}
      displayInternalSpeakers={displayInternalSpeakers}
      displayInconsistencies={sortedInconsistencies}
      // Transcript metadata
      transcriptMetadata={{
        otherSpeakers: savedTranscript?.otherSpeakers,
        deponent: savedTranscript?.deponent,
        createdDate: savedTranscript?.createdDate,
        transcriptName: savedTranscript?.name,
        phraseList: savedTranscript?.phraseList,
        objective: savedTranscript?.objective,
      }}
      transcriptContainerRef={transcriptContainerRef}
      isRecording={isRecording}
      isPaused={isPaused}
      isConnected={isConnected}
      isCompleting={isCompleting}
      isUserScrolling={isUserScrolling}
      handleScroll={handleScroll}
      scrollToBottom={scrollToBottom}
      scrollToTranscriptBlock={scrollToTranscriptBlock}
      handleScrollToBlockFromInconsistency={handleScrollToBlockFromInconsistency}
      editingSpeaker={editingSpeaker}
      editingName={editingName}
      startEditingSpeaker={startEditingSpeaker}
      cancelEditingSpeaker={cancelEditingSpeaker}
      saveSpeakerMapping={saveSpeakerMapping}
      setEditingName={setEditingName}
      updateBlock={updateBlock}
      acceptDiarizationCorrection={acceptDiarizationCorrection}
      rejectDiarizationCorrection={rejectDiarizationCorrection}
      startRecording={startRecording}
      pauseRecording={pauseRecording}
      completeRecording={completeRecording}
      isLoadingMode={isLoadingMode}
    />
  );
};

export default Transcription;
